import { MutationCondition, MutationRule, StepSchema } from '@feedyou-ai/feedbot-schema'
import {
  AnyObject,
  isNotEmptyElement,
  Mutable,
  MutableProps,
  SelectedMutations,
  updateMutation
} from '@feedyou/utils'

import { confirm } from '../../lib/bootbox'
import { actions as mutationActions } from '../../store/selectedMutations/selectedMutationsSlice'
import { toastNotifyBotMutationsUnselectedSuccess } from './toasts'
import { FeedbotDispatch } from '../../store/types'
import { confirmChangeOfStepStorage } from '../steps/utils/confirmChangeOfStepStorage'

export async function permitUnsetBotMutationsIfNeeded(
  name: string,
  selectedMutation: SelectedMutations,
  dispatch: FeedbotDispatch
): Promise<void> {
  const isConfirmed = await confirm(
    `Cannot add ${name} because some bot mutation is selected. Do you want to unselect bot mutations?`
  )

  if (!isConfirmed) {
    return
  }

  dispatch(mutationActions.bulkUnsetMutations(Object.keys(selectedMutation)))

  toastNotifyBotMutationsUnselectedSuccess()
}

export async function onClickWithBotMutationCheck(
  name: string,
  selectedMutations: SelectedMutations | undefined,
  dispatch: FeedbotDispatch,
  onClick: () => void
) {
  if (!isSomeSelectedMutation(selectedMutations)) {
    onClick()
    return
  }

  await permitUnsetBotMutationsIfNeeded(name, selectedMutations, dispatch)
}

export function isSomeSelectedMutation(
  selectedMutations?: SelectedMutations
): selectedMutations is NonNullable<typeof selectedMutations> {
  if (!selectedMutations) {
    return false
  }

  return Object.keys(selectedMutations).length !== 0
}

export function updateMutableValue<
  Step extends Mutable<StepSchema>,
  Field extends MutableProps<Step>,
  Value extends Step[Field]
>(
  step: Step,
  property: Field,
  value: Value,
  selectedMutations: SelectedMutations | undefined | null
): Step {
  if (!selectedMutations || !Object.values(selectedMutations).filter(isNotEmptyElement).length) {
    return { ...step, [property]: value }
  }

  return updateStepMutation(step, property, value, selectedMutations)
}

export function updateStepMutation<
  Step extends Mutable<StepSchema>,
  Field extends MutableProps<Step>,
  Value extends Step[Field]
>(step: Step, property: Field, value: Value, selectedMutations: SelectedMutations): Step {
  if (!value) {
    return step
  }

  const foundExistingMutationValue = getMutationValue(step, value, property, selectedMutations)

  if (foundExistingMutationValue) {
    return step
  }

  const originValue = step?.[property]
  const currentMutationValue = getMutationValue(step, undefined, property, selectedMutations)
  const isUpdateMutationNeeded = isUpdateMutationValueNeeded(value, currentMutationValue)

  if (originValue === value && !isUpdateMutationNeeded) {
    return step
  }

  return updateMutation(step, property, value, selectedMutations) as Step
}

export async function onNoteClick(
  storage: string | undefined = '',
  canEdit: boolean,
  isTestMode: boolean,
  setStorageSelectMode: (value: boolean) => void
): Promise<void> {
  const canSetStates = await shouldSetStorageStates(storage, canEdit, isTestMode)

  if (!canSetStates) {
    return
  }

  setStorageSelectMode(true)
}

async function shouldSetStorageStates(
  storage: string,
  canEdit: boolean,
  isTestMode: boolean
): Promise<boolean | undefined> {
  if (!canEdit) {
    return false
  }

  if (!storage) {
    return true
  }

  if (isTestMode) {
    return true
  }

  const isConfirmed = await confirmChangeOfStepStorage()

  if (isConfirmed) {
    return true
  }
}

function getMutationValue<
  Step extends Mutable<StepSchema>,
  Field extends MutableProps<Step>,
  Value extends Step[Field] | undefined
>(
  step: Step,
  input: Value,
  fieldType: Field,
  selectedMutations: SelectedMutations
): string | undefined {
  if (!step?.mutations) {
    return
  }

  for (const mutation of step.mutations as MutationRule[]) {
    const { test } = mutation

    if (!isSelectedTestValues(test, selectedMutations)) {
      continue
    }

    const foundValue = getSetValue(mutation, input, fieldType)

    if (!foundValue) {
      continue
    }

    return foundValue
  }
}

function getSetValue<
  Step extends Mutable<AnyObject>,
  Field extends MutableProps<Step>,
  Value extends Step[Field] | undefined
>(mutation: MutationRule, input: Value, fieldType: Field): string | undefined {
  if (!input) {
    return getCurrentSetValue(mutation, fieldType)
  }

  return getSetValueIfExists(mutation, input, fieldType)
}

function getCurrentSetValue(mutation: MutationRule, fieldType: string): string | undefined {
  return mutation?.set?.find(set => set.field === fieldType)?.value
}

function getSetValueIfExists(
  mutation: MutationRule,
  input: string | undefined,
  fieldType: string
): string | undefined {
  return mutation?.set?.find(set => set.value === input && set.field === fieldType)?.value
}

function isUpdateMutationValueNeeded(
  input: string | undefined,
  currentMutationValue: string | undefined
): boolean {
  if (!currentMutationValue) {
    return false
  }

  if (currentMutationValue === input) {
    return false
  }

  return true
}

function isSelectedTestValues(
  test: MutationCondition[] | undefined,
  selectedMutations: SelectedMutations
): boolean {
  const entries = Object.entries(selectedMutations)

  if (entries.length !== test?.length) {
    return false
  }

  for (const entry of Object.entries(selectedMutations)) {
    const [key, value] = entry

    const existsTest = test.some(test => test.field === key && test.value === value)

    if (!existsTest) {
      return false
    }
  }

  return true
}

// These constants need to be here just to prevent a top-level circular dependency
// between
export const stepWrapperPaddingLeftRightBottom = '21px'
export const stepWrapperPaddingTop = '15px'
